import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {Subject, Subscription} from 'rxjs';
import {
  CollectionUsersSettingsService,
} from '../../../../shared/shared-services/firebase/collection-users-settings.service';
import {Store} from '@ngrx/store';
import {
  path_users_userId_settings_stores_storeId_mailer_settings,
} from '../../../../shared/shared-services/database-paths';
import {IUser} from '../../../../features/user-access/models/user/user';
import {take, takeUntil} from 'rxjs/operators';
import {settingUserStoreMailerConfiguration} from '../../../../features/settings-new/store/setting.actions';
import {
  selectAllUsersEmailSettingForCurrentSelectedStore,
  selectIsUserEmailsLoading,
  selectMicrosoftMailBusyLabel,
} from '../../../../features/settings-new/store/setting.selectors';
import {getDeepCopyOfObject} from '../../../../shared/shared-utils/object/object.utils';
import {IStore} from '../../../../shared/shared-models/store/store';
import {selectUser} from '../../../../features/user-access/user-store/user.selectors';
import {selectSelectedStore} from '../../../../features/user-access/user-stores-store/user-store.selectors';
import {ModalControllerService} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {DEFAULT_LOADER_INFINITE} from '../../../../shared/shared-module-ui/models/animations';
import {IEmailSettings} from '../../../../features/settings-new/models';
import {IconColourEnum} from '../../../shared-icons/models/icons-colour.enum';
import {IconsEnum} from '../../../shared-icons/models/icons.enum';
import {ISelectOption} from '../../../../shared/shared-models/custom-select/custom-select';
import {
  MAIL_AUTH_MICROSOFT_365,
  MAIL_AUTH_SMTP,
  microsoftMailboxAddress,
  TMailAuthType,
} from '../../../../features/settings-new/models/email/microsoft-mail.utils';
import {environment} from '../../../../../environments/environment';

@Component({
  selector: 'app-automation-settings-email',
  templateUrl: './automation-settings-email.component.html',
  styleUrls: ['./automation-settings-email.component.scss'],
  standalone: false,
})
export class AutomationSettingsEmailComponent implements OnInit, OnDestroy {
  @Input() storeId: string;

  readonly icons = IconsEnum;
  protected readonly iconColourEnum = IconColourEnum;
  protected accountChoice: TMailAuthType = MAIL_AUTH_SMTP;
  protected microsoftMailBusyLabel: string | null = null;
  protected readonly microsoftMailConfigured = !!environment.microsoftMail.emailerOrigin;

  settings: Record<string, Record<string, IEmailSettings>> = {};

  storeName: string;
  saving: boolean;
  testing: string;

  hide: Record<string, boolean> = {mailer: true, iqLogin: true};

  settingsForm: FormGroup | null = null;
  formCache: Record<string, Record<string, IEmailSettings>> = {};
  changed: Record<string, Record<string, Record<string, boolean>>> = {};

  gmail: Record<string, Record<string, boolean>> = {};
  defaultStatus: Record<string, Record<string, boolean>> = {};
  copiedValue: IEmailSettings;
  default: Record<string, FormGroup> = {};

  readonly help = {
    mailer: {
      email: null,
      user: null,
      pwd: null,
      smtp: {port: null, server: null, sslOnly: null},
      imap: {port: null, server: null, sentBox: null, sslOnly: null},
    },
  };

  editableUsers: Record<string, IEmailSettings> = {};
  user: IUser;
  userId: string;
  openAccordion: string | null = null;

  isUserEmailSettingsLoading: boolean;
  selectedStore: IStore;
  protected hasInitiallyLoaded = false;

  protected readonly animation = DEFAULT_LOADER_INFINITE;
  hasChanges: boolean;
  private savedPassword: Record<string, string> = {};
  private readonly destroy$ = new Subject<boolean>();
  private enableImapSubscription: Subscription | undefined;
  private syncedServerAuthType: TMailAuthType | null = null;

  constructor(
    private modalController: ModalControllerService,
    private collectionUsersSettingsService: CollectionUsersSettingsService,
    private readonly store: Store,
  ) {
  }

  private static astrixArise(length: number): string {
    return '*'.repeat(length);
  }

  async ngOnInit(): Promise<void> {
    this.store.dispatch(settingUserStoreMailerConfiguration.getAllUsersMailerConfigurations());

    this.user = await this.store.select(selectUser).pipe(take(1)).toPromise();
    this.store.select(selectIsUserEmailsLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoading: boolean) => {
        this.isUserEmailSettingsLoading = isLoading;
        if (!isLoading) {
          this.hasInitiallyLoaded = true;
        }
      });
    this.store.select(selectMicrosoftMailBusyLabel)
      .pipe(takeUntil(this.destroy$))
      .subscribe((label: string | null) => {
        this.microsoftMailBusyLabel = label;
      });
    this.store.select(selectSelectedStore)
      .pipe(takeUntil(this.destroy$))
      .subscribe((store: IStore): void => {
        this.selectedStore = store;
      });

    this.defaultStatus[this.storeId] = {};
    this.settings[this.storeId] = {};
    this.gmail[this.storeId] = {};


    this.initializeEmailSettings();
    this.subscribeToEnableImapChanges();

  }

  ngOnDestroy(): void {
    this.enableImapSubscription?.unsubscribe();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  initializeEmailSettings(): void {
    this.store.select(selectAllUsersEmailSettingForCurrentSelectedStore)
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings: Record<string, IEmailSettings>) => {
      if (this.settingsForm && this.openAccordion) {
        const openSettings: IEmailSettings | undefined = settings?.[this.openAccordion];
        if (openSettings) {
          const openSettingsCopy: IEmailSettings = getDeepCopyOfObject(openSettings);
          this.editableUsers[this.openAccordion] = openSettingsCopy;
          this.settings[this.storeId][this.openAccordion] = getDeepCopyOfObject(openSettings);
          this.applySavedAccountChoice(openSettingsCopy.authType);
        }
        return;
      }

      this.settings[this.storeId] = {};
      this.defaultStatus[this.storeId] = {};
      this.changed[this.storeId] = {};
      this.formCache[this.storeId] = {};

      this.editableUsers = getDeepCopyOfObject({...settings ?? {}});

      for (const userId of Object.keys(this.editableUsers)) {
        this.changed[this.storeId][userId] = {};
        if (settings?.[userId]) {
          this.settings[this.storeId][userId] = settings[userId];

          if (settings[userId].email) {
            this.defaultStatus[this.storeId][userId] = false;
          } else {
            this.settings[this.storeId][userId] = {} as IEmailSettings;
          }
        } else {
          this.settings[this.storeId][userId] = {} as IEmailSettings;
          this.defaultStatus[this.storeId][userId] = true;
        }
        this.gmail[this.storeId][userId] = this.isGmail(
          this.settings[this.storeId][userId],
        );
      }
    });
  }

  close = (): void => {
    void this.modalController.dismiss().then();
  };

  onAccordionChange(isNowOpen: boolean, userId: string): void {
    if (this.settingsForm && this.userId) {
      if (!this.formCache[this.storeId]) {
        this.formCache[this.storeId] = {};
      }
      this.formCache[this.storeId][this.userId] = this.settingsForm.value;
    }

    if (!isNowOpen) {
      if (this.openAccordion === userId) {
        this.openAccordion = null;
        this.settingsForm = null;
        this.syncedServerAuthType = null;
      }
      return;
    }

    this.openAccordion = userId;
    this.userId = userId;

    const s: IEmailSettings = getDeepCopyOfObject(this.editableUsers[this.userId]);
    this.editableUsers[this.userId] = s;
    if (s.enableImap === undefined) {
      s.enableImap = true;
    }

    const req = Validators.required;
    const form: FormGroup = new FormGroup({
      email: new FormControl(s.email, [req]),
      user: new FormControl(
        s.user,
        Validators.pattern(/^[\w.-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
      ),
      pwd: new FormControl(
        AutomationSettingsEmailComponent.astrixArise(this.savedPassword?.[this.userId]?.length ?? (s.pwd?.length || 0)),
      ),
      smtp: new FormGroup({
        port: new FormControl(s.smtp?.port),
        server: new FormControl(s.smtp?.server),
        sslOnly: new FormControl(s.smtp?.sslOnly),
      }),
      enableImap: new FormControl(s.enableImap),
      imap: new FormGroup({
        server: new FormControl({value: s.imap?.server, disabled: false}),
        port: new FormControl({value: s.imap?.port, disabled: false}),
        sentBox: new FormControl({value: s.imap?.sentBox, disabled: false}),
        sslOnly: new FormControl({value: s.imap?.sslOnly, disabled: false}),
      }),
    });
    this.settingsForm = form;
    if (form.get('enableImap').value) {
      form.get('imap').enable();
    } else {
      form.get('imap').disable();
    }

    this.gmail[this.storeId][this.userId] = this.isGmail(s);

    if (this.gmail[this.storeId][this.userId]) {
      form.get('imap.server').setValue('');
      form.get('imap.port').setValue('');
      form.get('imap.sentBox').setValue('');
    }

    this.subscribeToEnableImapChanges();
    if (this.savedPassword[this.userId]) {
      form.get('pwd').setValue(this.savedPassword[this.userId]);
    }
    form.get('imap.sentBox').setValue('INBOX.Gallix');
    this.accountChoice = s.authType === MAIL_AUTH_MICROSOFT_365 ? MAIL_AUTH_MICROSOFT_365 : MAIL_AUTH_SMTP;
    this.syncedServerAuthType = this.accountChoice;
  }

  /**
   * Follows the saved sender only when that saved value changes.
   * A local Microsoft 365 selection that is not stored yet stays on screen.
   */
  private applySavedAccountChoice(authType: string | undefined): void {
    const saved: TMailAuthType = authType === MAIL_AUTH_MICROSOFT_365 ? MAIL_AUTH_MICROSOFT_365 : MAIL_AUTH_SMTP;
    if (saved === this.syncedServerAuthType) {
      return;
    }
    this.syncedServerAuthType = saved;
    this.accountChoice = saved;
  }

  protected accountOptions(): Record<string, ISelectOption> {
    return {
      smtp: {name: 'Standard', disabled: false, selected: this.accountChoice === MAIL_AUTH_SMTP},
      microsoft365: {name: 'Microsoft 365', disabled: false, selected: this.accountChoice === MAIL_AUTH_MICROSOFT_365},
    };
  }

  protected onAccountChange(selected: Record<string, ISelectOption>): void {
    const key: string | undefined = Object.keys(selected).find((optionKey: string) => selected[optionKey].selected);
    if (key !== MAIL_AUTH_SMTP && key !== MAIL_AUTH_MICROSOFT_365) {
      return;
    }
    if (key === this.accountChoice) {
      return;
    }
    this.accountChoice = key;
    const settings: IEmailSettings | undefined = this.editableUsers[this.userId];
    if (!settings) {
      return;
    }
    if (key === MAIL_AUTH_MICROSOFT_365 && settings.hasMicrosoftRefreshToken === true) {
      this.editableUsers[this.userId] = {
        ...settings,
        authType: MAIL_AUTH_MICROSOFT_365,
        connectedMailbox: settings.connectedMailbox || settings.email,
      };
      this.persistAuthType(MAIL_AUTH_MICROSOFT_365);
      return;
    }
    if (key === MAIL_AUTH_SMTP && settings.authType === MAIL_AUTH_MICROSOFT_365) {
      const nextSettings: IEmailSettings = {...settings, authType: MAIL_AUTH_SMTP};
      delete nextSettings.connectedMailbox;
      this.editableUsers[this.userId] = nextSettings;
      this.persistAuthType(MAIL_AUTH_SMTP);
    }
  }

  protected mailboxAddress(): string {
    return microsoftMailboxAddress(this.editableUsers[this.userId]);
  }

  protected microsoftConnected(): boolean {
    const settings: IEmailSettings | undefined = this.editableUsers[this.userId];
    return settings?.hasMicrosoftRefreshToken === true && this.mailboxAddress().length > 0;
  }

  protected microsoftActionsDisabled(): boolean {
    return !this.microsoftMailConfigured || !!this.microsoftMailBusyLabel || !this.userId || !this.storeId;
  }

  protected isSaveDisabled(): boolean {
    const form: FormGroup | null = this.settingsForm;
    if (!form || !this.openAccordion || this.saving || !!this.microsoftMailBusyLabel) {
      return true;
    }
    if (this.accountChoice === MAIL_AUTH_MICROSOFT_365) {
      return !this.microsoftConnected();
    }
    return form.invalid;
  }

  protected connectMicrosoft365(): void {
    if (this.microsoftActionsDisabled()) {
      return;
    }
    const email: string = (this.settingsForm.get('email')?.value as string | null) || '';
    this.store.dispatch(settingUserStoreMailerConfiguration.connectMicrosoftMail({
      loginHint: email.trim() || undefined,
      targetUserId: this.userId,
    }));
  }

  protected disconnectMicrosoft365(): void {
    if (this.microsoftActionsDisabled()) {
      return;
    }
    this.store.dispatch(settingUserStoreMailerConfiguration.disconnectMicrosoftMail({
      targetUserId: this.userId,
    }));
  }

  protected requestMicrosoftAdminConsent(): void {
    if (this.microsoftActionsDisabled()) {
      return;
    }
    this.store.dispatch(settingUserStoreMailerConfiguration.requestMicrosoftAdminConsent());
  }

  /**
   * Saves only the sender account. SMTP and IMAP fields already stored stay in place.
   */
  private persistAuthType(authType: TMailAuthType): void {
    if (!this.userId || !this.storeId) {
      return;
    }
    this.store.dispatch(settingUserStoreMailerConfiguration.setMailerAuthType({
      authType,
      storeId: this.storeId,
      userId: this.userId,
    }));
  }

  copy(): void {
    this.copiedValue = this.settingsForm.value as IEmailSettings;
    this.copiedValue.pwd = null;
  }

  paste(): void {
    if (this.copiedValue !== undefined) {
      this.settingsForm.setValue(this.copiedValue);
    }
  }

  savingCB(saving: boolean = true): void {
    this.saving = saving;
    const elements = document.getElementsByClassName('saving-disable');
    const opacity = saving ? '50%' : '100%';

    for (let i = 0; i < elements.length; i++) {
      (elements.item(i) as HTMLElement).style.opacity = opacity;
    }
  }

  changeEvt(
    event: Event,
    path: string,
    userId: string = this.userId,
    storeId: string = this.storeId,
  ): void {
    event.stopPropagation();
    const customEvent = event as CustomEvent<{value: string | boolean}>;
    const newValue = event.target instanceof HTMLInputElement
      ? event.target.value
      : customEvent.detail.value;
    this.hasChanges = true;

    if (path === 'pwd') {
      this.savedPassword[userId] = newValue as string;
      this.settingsForm.get(path).setValue(this.savedPassword[userId]);

    }


    if (path.endsWith('server')) {
      this.gmail[storeId][userId] = this.isGmail(
        this.settingsForm.value as IEmailSettings,
      );

      if (this.gmail[storeId][userId]) {
        this.settingsForm.get('imap.server').setValue('');
        this.settingsForm.get('imap.port').setValue('');
        this.settingsForm.get('imap.sentBox').setValue('');
      }
    }

    let v: IEmailSettings = this.settings[storeId][userId];
    if (!this.changed[storeId][userId]) {
      this.changed[storeId][userId] = {};
    }

    for (const p of path.split('.')) {
      if (v?.[p]) {
        v = v[p];
      } else {
        this.changed[storeId][userId][path] = true;
        return;
      }
    }
    this.changed[storeId][userId][path] = newValue !== v[path];
  }

  /**
   * Updates a boolean form control from app-switch-ui and marks the field as changed.
   * @param path - Dot-path to the form control (e.g. smtp.sslOnly)
   * @param checked - New toggle value
   * @param userId - User whose settings are being edited
   * @param storeId - Store whose settings are being edited
   */
  onToggleChange(
    path: string,
    checked: boolean,
    userId: string = this.userId,
    storeId: string = this.storeId,
  ): void {
    this.settingsForm.get(path)?.setValue(checked);
    this.hasChanges = true;

    if (!this.changed[storeId][userId]) {
      this.changed[storeId][userId] = {};
    }

    let original: unknown = this.settings[storeId][userId];
    for (const segment of path.split('.')) {
      if (original && typeof original === 'object' && segment in (original)) {
        original = (original as Record<string, unknown>)[segment];
      } else {
        this.changed[storeId][userId][path] = true;
        return;
      }
    }
    this.changed[storeId][userId][path] = checked !== original;
  }

  save(storeId: string = this.storeId, userId: string = this.userId): void {
    if (!this.openAccordion || !this.settingsForm) {
      return;
    }
    const form: FormGroup = this.settingsForm;
    if (this.accountChoice === MAIL_AUTH_MICROSOFT_365) {
      void this.saveMicrosoftAccount(storeId, userId);
      return;
    }
    if (form.invalid) {
      return;
    }
    const updates = form.value as IEmailSettings;
    updates.authType = MAIL_AUTH_SMTP;
    updates.status = 'PENDING';
    if (!(updates.imap?.server && updates.imap.port)) {
      delete updates.imap;
    }

    if (updates.pwd) {
      let valid = false;
      for (const ch of updates.pwd) {
        if (ch !== '*') {
          valid = true;
          updates.encryptedPwd = false;
          break;
        }
      }

      if (!valid) {
        delete updates.pwd;
      }
    }

    if (Object.keys(updates).length) {
      this.savingCB();
      this.saving = true;
      void this.collectionUsersSettingsService
        .setDocument<IEmailSettings>(
          path_users_userId_settings_stores_storeId_mailer_settings(userId, storeId),
          updates,
        )
        .then(() => {
          this.savingCB(false);
          this.saving = false;
          this.hasChanges = false;
          this.testSelectedAccount(storeId, userId, MAIL_AUTH_SMTP);
        });
    }
  }

  /**
   * Saves Microsoft 365 as the sender and tests that account.
   * Standard SMTP and IMAP fields already stored are left in place.
   */
  private async saveMicrosoftAccount(storeId: string, userId: string): Promise<void> {
    if (!this.microsoftConnected()) {
      return;
    }
    this.savingCB();
    this.saving = true;
    await this.collectionUsersSettingsService.setDocument(
      path_users_userId_settings_stores_storeId_mailer_settings(userId, storeId),
      {authType: MAIL_AUTH_MICROSOFT_365, status: 'PENDING'},
    );
    this.savingCB(false);
    this.saving = false;
    this.testSelectedAccount(storeId, userId, MAIL_AUTH_MICROSOFT_365);
  }

  /**
   * Runs the email test for the account selected in this form.
   */
  private testSelectedAccount(storeId: string, userId: string, authType: TMailAuthType): void {
    this.store.dispatch(settingUserStoreMailerConfiguration.testMailerConfiguration({
      authType,
      storeId,
      userId,
    }));
  }


  invalid(): void {
    const dig = (fg: FormGroup): void => {
      for (const key of Object.keys(fg.controls)) {
        const control = fg.controls[key];

        if (control instanceof FormGroup) {
          dig(control);
        }
      }
    };
    dig(this.settingsForm);
  }

  private subscribeToEnableImapChanges(): void {
    this.enableImapSubscription?.unsubscribe();
    const enableImapControl = this.settingsForm?.get('enableImap');
    if (!enableImapControl) {
      return;
    }
    this.enableImapSubscription = enableImapControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((isEnabled: boolean) => {
        const imapGroup = this.settingsForm.get('imap');
        if (isEnabled) {
          imapGroup.enable();
        } else {
          imapGroup.disable();
        }
      });
  }

  private isGmail(emailInfo: IEmailSettings): boolean {
    return !!emailInfo && (
      (typeof emailInfo.imap?.server === 'string' && emailInfo.imap.server.toLowerCase().includes('gmail.com')) ||
      (typeof emailInfo.smtp?.server === 'string' && emailInfo.smtp.server.toLowerCase().includes('gmail.com'))
    );
  }

}
